import React, { useCallback, useEffect, useState } from 'react';
import {
  SparklesIcon, ShieldCheckIcon, ClockIcon, ArrowPathIcon, MicrophoneIcon, ChatBubbleLeftIcon,
} from '@heroicons/react/24/outline';
import assistantService from '../../services/assistantService';
import { errText } from '../../services/scheduleService';
import AiConfigModal from './AiConfigModal';
import PracticeDetails from './PracticeDetails';
import EmailSettings from './EmailSettings';

const STATUS_TONE = {
  executed: 'bg-green-100 text-green-700',
  proposed: 'bg-amber-100 text-amber-700',
  answered: 'bg-blue-100 text-blue-700',
  clarify: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-slate-200 text-slate-500',
  rejected: 'bg-red-100 text-red-700',
  error: 'bg-red-100 text-red-700',
};

/**
 * Settings (admin): practice details and hospital practice numbers, email, and the AI assistant — which provider it uses, which
 * procedures it may run (the allow-list), and what it has been doing.
 */
const Settings = () => {
  const [aiOpen, setAiOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [procs, setProcs] = useState([]);
  const [log, setLog] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const [s, p, l] = await Promise.all([assistantService.status(), assistantService.catalogue(), assistantService.log()]);
      setStatus(s);
      setProcs(p);
      setLog(l);
    } catch (e) {
      setError(errText(e));
      // Don't leave the card saying "Checking…" forever when the request failed.
      setStatus((s) => s || { ready: false, reason: 'Could not reach the assistant service. If the backend was just updated, restart it.' });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchProc = async (proc, patch) => {
    setSaving(proc.proc_code);
    try {
      const updated = await assistantService.updateProcedure(proc.proc_code, patch);
      setProcs((prev) => prev.map((p) => (p.proc_code === proc.proc_code ? { ...p, ...updated } : p)));
    } catch (e) {
      setError(errText(e));
    } finally {
      setSaving('');
    }
  };

  const toggleRole = (proc, role) => {
    const has = proc.allowed_roles.includes(role);
    const next = has ? proc.allowed_roles.filter((r) => r !== role) : [...proc.allowed_roles, role];
    if (!next.length) return;
    patchProc(proc, { allowed_roles: next });
  };

  return (
    <div className="mx-auto max-w-5xl p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-brand-deep">Settings</h1>
        <p className="mt-1 text-slate-500">Practice configuration and integrations.</p>
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <PracticeDetails />
      <EmailSettings />

      {/* AI provider */}
      <section className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-gradient text-white">
            <SparklesIcon className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-bold text-slate-800">AI assistant</h2>
            <p className="text-sm text-slate-500">
              {status === null
                ? 'Checking…'
                : status.ready
                  ? <>Connected — using <strong>{status.model}</strong> via {status.provider}.</>
                  : status.reason}
            </p>
          </div>
          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${status?.ready ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}>
            {status?.ready ? 'Ready' : 'Not connected'}
          </span>
          <button onClick={() => setAiOpen(true)} className="rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95">
            Configure provider
          </button>
        </div>
      </section>

      {/* Procedure catalogue */}
      <section className="mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
          <ShieldCheckIcon className="mt-0.5 h-5 w-5 text-brand-teal" />
          <div>
            <h2 className="font-bold text-slate-800">Actions the assistant may run</h2>
            <p className="text-sm text-slate-500">
              The assistant can only call these approved database procedures — it never writes queries and has no other access. Switch one off and the model is no longer told it exists.
              Changes always ask the user to confirm first.
            </p>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-5 py-2.5 font-semibold">Action</th>
              <th className="px-3 py-2.5 font-semibold">Kind</th>
              <th className="px-3 py-2.5 font-semibold">Who</th>
              <th className="px-5 py-2.5 text-right font-semibold">Enabled</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {procs.map((p) => (
              <tr key={p.proc_code} className={p.is_enabled ? '' : 'bg-slate-50/60'}>
                <td className="px-5 py-3">
                  <div className={`font-semibold ${p.is_enabled ? 'text-slate-800' : 'text-slate-400'}`}>{p.title}</div>
                  <div className="text-xs text-slate-500">{p.description}</div>
                  <code className="text-[10px] text-slate-400">{p.proc_code} → {p.function_name}</code>
                </td>
                <td className="px-3 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${p.kind === 'write' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                    {p.kind === 'write' ? 'Changes data' : 'Read only'}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <div className="flex gap-1">
                    {['doctor', 'admin'].map((role) => {
                      const on = p.allowed_roles.includes(role);
                      return (
                        <button
                          key={role}
                          disabled={saving === p.proc_code || (on && p.allowed_roles.length === 1)}
                          onClick={() => toggleRole(p, role)}
                          title={on && p.allowed_roles.length === 1 ? 'At least one role is needed — switch the action off instead' : undefined}
                          className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${on ? 'border-brand-navy bg-brand-navy text-white' : 'border-slate-300 text-slate-400'} disabled:cursor-not-allowed`}
                        >
                          {role === 'doctor' ? 'Doctors' : 'Admins'}
                        </button>
                      );
                    })}
                  </div>
                </td>
                <td className="px-5 py-3 text-right">
                  <button
                    role="switch"
                    aria-checked={p.is_enabled}
                    disabled={saving === p.proc_code}
                    onClick={() => patchProc(p, { is_enabled: !p.is_enabled })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${p.is_enabled ? 'bg-brand-teal' : 'bg-slate-300'}`}
                  >
                    <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition ${p.is_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </td>
              </tr>
            ))}
            {procs.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-6 text-center text-slate-400">No procedures catalogued — run backend/db/05_ai_assistant.sql.</td></tr>
            )}
          </tbody>
        </table>
      </section>

      {/* Activity */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-4">
          <ClockIcon className="h-5 w-5 text-brand-teal" />
          <div className="flex-1">
            <h2 className="font-bold text-slate-800">Recent assistant activity</h2>
            <p className="text-sm text-slate-500">Every request, what was proposed and what actually ran. Last 100.</p>
          </div>
          <button onClick={load} title="Refresh" className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-brand-navy">
            <ArrowPathIcon className="h-5 w-5" />
          </button>
        </div>
        {log.length === 0 ? (
          <p className="px-5 py-6 text-center text-sm text-slate-400">No requests yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {log.map((e) => (
              <li key={e.log_id} className="flex gap-3 px-5 py-3 text-sm">
                <span className="mt-0.5 text-slate-400" title={e.channel}>
                  {e.channel === 'voice' ? <MicrophoneIcon className="h-4 w-4" /> : <ChatBubbleLeftIcon className="h-4 w-4" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-700">{e.user_name || 'Unknown user'}</span>
                    <span className="text-xs text-slate-400">{new Date(e.created_at).toLocaleString('en-ZA')}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_TONE[e.status] || 'bg-slate-100 text-slate-500'}`}>{e.status}</span>
                    {e.duration_ms != null && <span className="text-[11px] text-slate-400">{(e.duration_ms / 1000).toFixed(1)}s · {e.model_code}</span>}
                  </div>
                  <p className="mt-0.5 text-slate-700">“{e.transcript}”</p>
                  {(e.results || []).map((r, i) => (
                    <p key={i} className="text-xs text-slate-500">→ {r.summary || r.proc}</p>
                  ))}
                  {e.error && <p className="text-xs text-red-600">{e.error}</p>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {aiOpen && <AiConfigModal onClose={() => setAiOpen(false)} onChanged={load} />}
    </div>
  );
};

export default Settings;
